import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { Command } from '../../../menu/types';
import { useGetGridMetadataQuery } from '../../../menu';
import { useGetGridDataQuery, useGetRelationCommandMetadataQuery } from '../../api/gridApi';
import type { ColumnMetadata, GridFilter } from '../../types';
import { normalizeGridKey, parseColumnMetadata } from '../../utils';
import { extractErrorMessage } from '../../../../utils/errorUtils';

export type RelationPickerTarget = {
  gridKey: string;
  targetRows: Record<string, unknown>[];
  selectedRows?: Set<string>;
  hasUnsavedChanges?: boolean;
  onAddRowWithData: (fieldData: Record<string, unknown> | Record<string, unknown>[]) => void;
  // When true, already-linked rows can be unchecked to remove them.
  // onAddRowWithData will receive rows with _action: 'add' | 'remove'.
  allowDeselect?: boolean;
};

type RelationPickerDialogProps = {
  command: Command | null;
  parentId?: string;
  relationTargets: Record<string, RelationPickerTarget>;
  onClose: () => void;
};

const EMPTY_ROWS: Record<string, unknown>[] = [];
const EMPTY_LOOKUP_MAP: Record<string, Record<string, string>> = {};

export const RelationPickerDialog = ({
  command,
  parentId,
  relationTargets,
  onClose,
}: RelationPickerDialogProps) => {
  const open = Boolean(command);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);

  // Tracks only rows the user explicitly toggled in this session.
  // existingIds (already-linked rows) are pre-checked separately via plain derivation.
  const [userAdded, setUserAdded] = useState<Set<string>>(new Set());

  // Tracks existing rows the user explicitly unchecked (only when allowDeselect=true).
  const [userRemoved, setUserRemoved] = useState<Set<string>>(new Set());

  const [selectedDate, setSelectedDate] = useState('');

  const metadataQuery = useGetRelationCommandMetadataQuery(
    { commandKey: command?.key ?? '' },
    { skip: !open || !command?.key }
  );
  const relationConfig = metadataQuery.data;

  const sourceMetadataQuery = useGetGridMetadataQuery(
    {
      nodeKey: relationConfig?.relatedGridKey ?? '',
      ...(parentId ? { parentId } : {}),
    },
    { skip: !open || !relationConfig?.relatedGridKey || !parentId }
  );
  const sourceMetadata = sourceMetadataQuery.data;

  const sourceColumns = useMemo<ColumnMetadata[]>(
    () => parseColumnMetadata(sourceMetadata?.columns),
    [sourceMetadata?.columns]
  );
  const visibleColumns = useMemo(
    () => sourceColumns.filter((column) => column.visible !== false),
    [sourceColumns]
  );

  const pageSize = sourceMetadata?.numberOfRowsPerPage ?? 0;
  const useServerPaging = sourceMetadata?.usePaging === true && pageSize > 0;

  const serverFilter = useMemo<GridFilter | undefined>(() => {
    const term = searchTerm.trim();
    if (!useServerPaging || !term) return undefined;

    const fields = sourceColumns
      .map((column) => column.field || column.dataField)
      .filter((field): field is string => Boolean(field));
    if (fields.length === 0) return undefined;

    const filter: unknown[] = [];
    fields.forEach((field, index) => {
      if (index > 0) filter.push('or');
      filter.push([field, 'contains', term]);
    });
    return filter as GridFilter;
  }, [searchTerm, sourceColumns, useServerPaging]);

  const gridQueryArgs = useMemo(
    () => ({
      options: {
        nodeKey: relationConfig?.relatedGridKey ?? '',
        parentId,
        ...(useServerPaging ? { skip: page * pageSize, take: pageSize } : {}),
        ...(serverFilter ? { filter: serverFilter } : {}),
      },
      columnMetadata: sourceColumns,
    }),
    [
      relationConfig?.relatedGridKey,
      parentId,
      page,
      pageSize,
      serverFilter,
      sourceColumns,
      useServerPaging,
    ]
  );

  const sourceDataQuery = useGetGridDataQuery(gridQueryArgs, {
    skip: !open || !relationConfig?.relatedGridKey || !parentId || !sourceMetadata,
  });

  const targetField = relationConfig?.targetGridField ?? '';
  const sourceKeyField = relationConfig?.sourceKeyField || 'Id';
  const targetGridKey = normalizeGridKey(relationConfig?.relatedTargetGridKey);
  const relationTarget = targetGridKey
    ? Object.values(relationTargets).find(
        (target) => normalizeGridKey(target.gridKey) === targetGridKey
      )
    : undefined;
  const targetRows = relationTarget?.targetRows ?? EMPTY_ROWS;
  const allowDeselect = relationTarget?.allowDeselect ?? false;

  // Derived without useMemo — React Compiler handles optimization.
  // Manual useMemo on targetRows triggers mutation warnings from the compiler
  // because targetRows is an external array reference.
  const existingIds = new Set(
    targetRows
      .map((row) => {
        const value = row[targetField];
        return value !== undefined && value !== null && String(value).trim() !== ''
          ? String(value)
          : null;
      })
      .filter((v): v is string => v !== null)
  );

  // Already-linked (existingIds) + user additions - user removals
  const selectedIds = new Set([...existingIds, ...userAdded].filter((id) => !userRemoved.has(id)));

  const sourceRows = sourceDataQuery.data?.data ?? EMPTY_ROWS;
  const lookupMaps = sourceDataQuery.data?.lookupMaps ?? EMPTY_LOOKUP_MAP;

  const getSourceId = (row: Record<string, unknown>) => {
    const configuredValue = row[sourceKeyField];
    return String(configuredValue ?? row.Id ?? '');
  };

  const getDisplayValue = useCallback(
    (row: Record<string, unknown>, column: ColumnMetadata) => {
      const fieldName = column.field || column.dataField || '';
      const rawValue = row[fieldName];
      const lookup = lookupMaps[fieldName]?.[String(rawValue ?? '')];
      return String(lookup ?? rawValue ?? '');
    },
    [lookupMaps]
  );

  const filteredRows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return sourceRows;

    return sourceRows.filter((row) =>
      sourceColumns.some((column) => getDisplayValue(row, column).toLowerCase().includes(term))
    );
  }, [searchTerm, sourceRows, sourceColumns, getDisplayValue]);

  const visibleRows = useServerPaging ? sourceRows : filteredRows;
  const totalCount = useServerPaging
    ? (sourceDataQuery.data?.totalCount ?? sourceRows.length)
    : filteredRows.length;
  const totalPages = useServerPaging ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1;

  // When allowDeselect, all visible rows are selectable (including existing).
  // Otherwise only non-existing rows can be toggled.
  const selectableVisibleIds = visibleRows
    .map(getSourceId)
    .filter((id) => id && (allowDeselect || !existingIds.has(id)));

  const allVisibleSelected =
    selectableVisibleIds.length > 0 && selectableVisibleIds.every((id) => selectedIds.has(id));

  // Shared close handler — resets all session state so reopening starts fresh
  const handleClose = () => {
    setUserAdded(new Set());
    setUserRemoved(new Set());
    setSearchTerm('');
    setPage(0);
    setSelectedDate('');
    onClose();
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(0);
  };

  const toggleRow = (id: string) => {
    if (!id) return;

    if (existingIds.has(id)) {
      // Only allow toggling existing rows when allowDeselect is enabled
      if (!allowDeselect) return;
      setUserRemoved((current) => {
        const next = new Set(current);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      return;
    }

    setUserAdded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleVisibleRows = () => {
    const shouldSelect = !allVisibleSelected;

    if (allowDeselect) {
      const allVisibleIds = visibleRows.map(getSourceId).filter(Boolean);

      setUserRemoved((current) => {
        const next = new Set(current);
        allVisibleIds
          .filter((id) => existingIds.has(id))
          .forEach((id) => (shouldSelect ? next.delete(id) : next.add(id)));
        return next;
      });

      setUserAdded((current) => {
        const next = new Set(current);
        allVisibleIds
          .filter((id) => !existingIds.has(id))
          .forEach((id) => (shouldSelect ? next.add(id) : next.delete(id)));
        return next;
      });
      return;
    }

    // Original behavior — only non-existing rows
    setUserAdded((current) => {
      const next = new Set(current);
      selectableVisibleIds.forEach((id) => (shouldSelect ? next.add(id) : next.delete(id)));
      return next;
    });
  };

  const handleConfirm = () => {
    if (!relationConfig || !relationTarget) return;
    if (userAdded.size === 0 && userRemoved.size === 0) {
      handleClose();
      return;
    }

    const addedRows = Array.from(userAdded).map((id) => ({
      [relationConfig.targetGridField]: id,
      _action: 'add' as const,
      ...(relationConfig.showDatePicker && relationConfig.targetDateField && selectedDate
        ? { [relationConfig.targetDateField]: selectedDate }
        : {}),
    }));

    const removedRows = Array.from(userRemoved).map((id) => ({
      [relationConfig.targetGridField]: id,
      _action: 'remove' as const,
    }));

    relationTarget.onAddRowWithData([...addedRows, ...removedRows]);
    handleClose();
  };

  const isTargetUnavailable = Boolean(relationConfig && !relationTarget);
  const isLoading =
    metadataQuery.isLoading || sourceMetadataQuery.isLoading || sourceDataQuery.isLoading;
  const error = metadataQuery.error || sourceMetadataQuery.error || sourceDataQuery.error;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {relationConfig?.popupCaption || command?.caption || 'Add relations'}
      </DialogTitle>
      <DialogContent dividers>
        {relationConfig?.popupText && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {relationConfig.popupText}
          </Typography>
        )}

        {isTargetUnavailable && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Add Relations target grid is not available on this detail form.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {extractErrorMessage(error, 'Failed to load relation options.')}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : !error && !isTargetUnavailable ? (
          <>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                value={searchTerm}
                onChange={(event) => handleSearchChange(event.target.value)}
              />
              {relationConfig?.showDatePicker && (
                <TextField
                  size="small"
                  type="date"
                  label={relationConfig.targetDateField || 'Date'}
                  value={selectedDate}
                  onChange={(event) => setSelectedDate(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </Box>

            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allVisibleSelected}
                      onChange={toggleVisibleRows}
                      disabled={selectableVisibleIds.length === 0}
                    />
                  </TableCell>
                  {visibleColumns.map((column) => (
                    <TableCell key={column.field || column.dataField}>
                      {column.caption || column.title || column.field || column.dataField}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + 1} align="center">
                      No results found.
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((row) => {
                    const id = getSourceId(row);
                    const alreadyAdded = existingIds.has(id);
                    return (
                      <TableRow key={id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.has(id)}
                            disabled={alreadyAdded && !allowDeselect}
                            onChange={() => toggleRow(id)}
                          />
                        </TableCell>
                        {visibleColumns.map((column) => (
                          <TableCell key={column.field || column.dataField}>
                            {getDisplayValue(row, column)}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>

            {useServerPaging && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Page {totalCount === 0 ? 0 : page + 1} of {totalPages} ({totalCount} items)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                    disabled={page === 0}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    onClick={() => setPage((current) => current + 1)}
                    disabled={(page + 1) * pageSize >= totalCount}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={
            isLoading ||
            Boolean(error) ||
            isTargetUnavailable ||
            !relationConfig ||
            !relationTarget ||
            (userAdded.size === 0 && userRemoved.size === 0)
          }
        >
          Add selected
        </Button>
      </DialogActions>
    </Dialog>
  );
};
